import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useCallback, useState } from 'react'
import {
  actionTypeWrapper,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  isLoading,
  LoadingTypes,
  startLoading,
  stopLoading,
  useAccountId,
  useCloudAccounts,
  useCurrentCloudProvider,
} from '@labrav/common'
import { CloudAccountType, ColumnMenuOption } from '@labrav/react-components'
import { Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import type { ComponentLoadingState } from '@labrav/common'

import { CreateNewListingButton } from '../../../components/ProductListingV2/CreateNewListingButton'
import { AddProductModal } from '../AddProductModal/AddProductModal'
import { setSelectedProductOnListing } from '../../../modules/productsListing/action'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { FlyOutListingUserType } from '../../../types/external'
import { CloudAccountChangeParams, CloudAccountChangeV2 } from '@labrav/common'
import EmptyProductListing from '../../images/EmptyProductListing.png'
import { useStyles } from './ListingCreationPage.styles'

type LoadingRootState = {
  loading: ComponentLoadingState
}

export const ListingCreationPage: React.FC = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const {
    products: productRows,
    additionalAllowedProducts,
    plan,
  } = useFlyOutProductsBasedOnSelectedCloud()
  const navigate = useNavigate()
  const [openAddProductModal, setOpenAddProductModal] = useState<boolean>(false)

  const { accountId: routeAccountId, cloud: routeCloud } = useParams<{
    cloud: string
    accountId: string
  }>()

  const defaultAccountId = useAccountId({ product: 'flyout' })
  const currentAccountId = routeAccountId || defaultAccountId
  const marketplaceCloudAccounts = useCloudAccounts('flyout')
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId || '',
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const showAddProductModal = () => {
    setOpenAddProductModal(true)
  }
  const handleCloseAddProductModal = () => {
    setOpenAddProductModal(false)
  }

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params
      const cloudPath = cloud.toLowerCase()
      navigate(`/flyout/${cloudPath}/${originalAccountId}/products-listing`, {
        replace: true,
      })

      await dispatch(startLoading(LoadingTypes.GENERAL) as never)
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        ) as never
      )
      await dispatch(setCloudMarketplaceProductListing(cloud) as never)
      await dispatch(stopLoading(LoadingTypes.GENERAL) as never)
    },
    [dispatch, navigate, selectedCloudTypeProductListing]
  )

  const isFreeTier = plan === 'free_tier'
  const enableToCreate = isFreeTier
    ? additionalAllowedProducts !== undefined && additionalAllowedProducts > 0
    : true

  const loading = useSelector((state: LoadingRootState) =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const handleCreateListingSelection = (option: ColumnMenuOption) => {
    if (option.key === 'create-new-listing') {
      showAddProductModal()
    } else {
      const cloudPath =
        routeCloud?.toLowerCase() ||
        selectedCloudTypeProductListing.toLowerCase()
      navigate(
        `/flyout/${cloudPath}/${currentAccountId}/products-listing/edit?listingTypeInfo=${FlyOutListingUserType.MIGRATED}`
      )
    }
  }

  return (
    <>
      <div
        className={classes.emptyProductContainer}
        data-testid="empty-product-listing"
      >
        <div className={classes.emptyProductInnerContainer}>
          <div className={classes.leftContainer} data-testid="image-container">
            <img
              className={classes.thumbnailImages}
              src={EmptyProductListing}
              alt={'empty-product-listing'}
            />
          </div>
          <div
            className={classes.rightContainer}
            data-testid="drop-down-container"
          >
            <Typography className={classes.heading}>
              Start creating your first product
            </Typography>
            <div className={classes.subHeadingContainer}>
              <Typography className={classes.subHeading}>
                Begin your cloud marketplace journey by creating your first
                listing.
              </Typography>
              <Typography className={classes.subHeading}>
                Expand your sales channel, unlock new markets and customers,
                simplify your software delivery and more such benefits are
                waiting for you.
              </Typography>
            </div>
            <div className={classes.dropDownContainer}>
              <div className={classes.cloudChangeContainer}>
                <CloudAccountChangeV2
                  cloudAccounts={marketplaceCloudAccounts as CloudAccountType[]}
                  selectedAccountId={selectedAccountIdWithCloud}
                  onChange={handleCloudAccountChange}
                  width={220}
                  size="small"
                />
              </div>
              <div className={classes.toolTipView}>
                <CreateNewListingButton
                  isFreeTier={isFreeTier}
                  enableToCreate={enableToCreate}
                  loading={loading}
                  handleCreateListingSelection={handleCreateListingSelection}
                  selectedCloudTypeProductListing={
                    selectedCloudTypeProductListing
                  }
                  productRows={productRows}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddProductModal
        openAddProductModal={openAddProductModal}
        handleCloseAddProductModal={handleCloseAddProductModal}
      />
    </>
  )
}
